import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

// 2026-10-15 — chat: "Can we move these things to Settings page? or Users page?
// Follow strategies can be checkbox for each setup instead of textbox."
// Admin-only controls for a linked (family) Zerodha account: approval, dry-run /
// live switch, limits, and which autopilot strategies its account copies
// (backend kite_accounts.update_controls / kite_mirror.py). Shown on
// Admin → Users under each user that has Zerodha credentials saved.

export const STOCK_STRATEGIES = [
  ["sniper", "Sniper"], ["cb", "Candle Breakout"], ["vwap", "VWAP Bounce"],
  ["momentum_oi", "Momentum OI"], ["oi", "OI Autopilot"], ["setup_e", "Setup E"],
];
const COMMODITIES = [["CRUDEOIL", "Crude Oil"], ["SILVERM", "Silver Mini"], ["GOLDM", "Gold Mini"]];
const COMMODITY_STRATS = [["reversal", "Reversal"], ["vwap_momentum", "VWAP Momentum"]];
export const COMMODITY_STRATEGIES = COMMODITIES.flatMap(([u, ul]) =>
  COMMODITY_STRATS.map(([s, sl]) => [`commodity:${u}:${s}`, `${ul} · ${sl}`]));
const ALL_IDS = [...STOCK_STRATEGIES, ...COMMODITY_STRATEGIES].map(([id]) => id);

// a saved list may contain wildcards ("*", "commodity:*") from the old text box
export const expandStrategies = (list) => {
  const out = new Set();
  (list || []).forEach((raw) => {
    const s = String(raw).trim().toLowerCase();
    if (s === "*") ALL_IDS.forEach((id) => out.add(id));
    else if (s.endsWith("*")) ALL_IDS.filter((id) => id.toLowerCase().startsWith(s.slice(0, -1))).forEach((id) => out.add(id));
    else {
      const match = ALL_IDS.find((id) => id.toLowerCase() === s);
      if (match) out.add(match);
    }
  });
  return out;
};

const btn = "px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-40";

const stateOf = (acc) => ({
  max_margin_rs: acc.max_margin_rs ?? 40000, max_orders_per_day: acc.max_orders_per_day ?? 4,
  lots_multiplier: acc.lots_multiplier ?? 1, live_enabled: !!acc.live_enabled,
  max_daily_loss_rs: acc.max_daily_loss_rs ?? 3000,
  strategies: expandStrategies(acc.mirror_strategies),
});

export const LinkedKiteControls = ({ acc, onSaved, user }) => {
  const [f, setF] = useState(stateOf(acc));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(stateOf(acc)); }, [acc.account_id, acc.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { strategies, ...rest } = f;
      await api.put(`/kite-accounts/${acc.account_id}/controls`,
        { ...rest, mirror_strategies: ALL_IDS.filter((id) => strategies.has(id)), ...extra });
      toast.success(extra.enabled === true ? `${acc.label} enabled` : extra.enabled === false ? `${acc.label} disabled` : "Saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id) => {
    const s = new Set(f.strategies);
    if (s.has(id)) s.delete(id); else s.add(id);
    setF({ ...f, strategies: s });
  };
  const num = (k, label, w = "w-24") => (
    <label className="flex items-center gap-1.5">
      <span className="text-muted-3">{label}</span>
      <input type="number" className={`bg-transparent border border-grid px-1.5 py-0.5 ${w}`} value={f[k]}
             onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );
  const box = ([id, label]) => (
    <label key={id} className="flex items-center gap-1.5 min-w-[150px]">
      <input type="checkbox" checked={f.strategies.has(id)} onChange={() => toggle(id)} data-testid={`follow-${id}`} />
      <span>{label}</span>
    </label>
  );
  const stockOff = user && !user.live_trading_enabled;
  const mcxOff = user && !user.commodity_live_trading_enabled;

  return (
    <div className="space-y-3 text-[11px] font-mono-data" data-testid={`linked-kite-controls-${acc.account_id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-muted-3 uppercase text-[9px] tracking-[0.2em]">Zerodha copy · {acc.kite_user_id || "—"}</span>
        <span style={{ color: acc.connected ? "var(--bull)" : "var(--bear)" }}>
          {acc.connected ? "● logged in today" : "● not logged in today"}
        </span>
        {acc.enabled ? (
          <button className={btn} style={{ color: "var(--bear)", borderColor: "var(--bear)" }} disabled={busy}
                  onClick={() => window.confirm(`Stop copying orders to ${acc.label}?`) && save({ enabled: false })}>Disable</button>
        ) : (
          <button className={btn} style={{ color: "var(--bull)", borderColor: "var(--bull)" }} disabled={busy}
                  onClick={() => window.confirm(`Enable ${acc.label}'s Zerodha account for copied autopilot orders?`) && save({ enabled: true })}>Enable</button>
        )}
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={f.live_enabled} onChange={(e) => setF({ ...f, live_enabled: e.target.checked })} />
          <span style={{ color: f.live_enabled ? "var(--bull)" : undefined }}>
            Real orders {f.live_enabled ? "ON" : "OFF (dry run — logged only)"}
          </span>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {num("max_margin_rs", "Max margin / order ₹")}
        {num("max_orders_per_day", "Auto orders / day (manual not capped)", "w-14")}
        {num("lots_multiplier", "Lots ×", "w-14")}
        {num("max_daily_loss_rs", "Daily loss stop ₹", "w-20")}
      </div>
      <div className="space-y-1">
        <div className="text-muted-3 uppercase text-[9px] tracking-[0.2em]">
          Copy these stock setups {stockOff && <span style={{ color: "var(--bear)" }}>· LIVE is OFF for this user, nothing will copy</span>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">{STOCK_STRATEGIES.map(box)}</div>
      </div>
      <div className="space-y-1">
        <div className="text-muted-3 uppercase text-[9px] tracking-[0.2em]">
          Copy these commodity strategies {mcxOff && <span style={{ color: "var(--bear)" }}>· COMMODITY LIVE is OFF for this user, nothing will copy</span>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">{COMMODITY_STRATEGIES.map(box)}</div>
      </div>
      <div className="flex items-center gap-3">
        <button className={btn} style={{ color: "var(--bull)", borderColor: "var(--bull)" }} disabled={busy} onClick={() => save()}>
          Save
        </button>
        <span className="text-[10px] text-muted-3">
          Only orders that actually go live on your account are copied, at the same moment. Login, positions and
          manual orders stay on Commodities → Live Orders.
        </span>
      </div>
    </div>
  );
};

export default LinkedKiteControls;
